import type { ReactNode } from "react";
import type { Group } from "../types";

interface GroupFilterBarProps {
  groups: Group[];
  value: number | null;
  onChange: (groupId: number | null) => void;
  totalCount?: number;
}

export function GroupFilterBar({ groups, value, onChange, totalCount }: GroupFilterBarProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <FilterButton active={value === null} onClick={() => onChange(null)}>
        All{totalCount !== undefined ? ` (${totalCount})` : ""}
      </FilterButton>
      {groups.map((g) => (
        <FilterButton
          key={g.id}
          active={value === g.id}
          onClick={() => onChange(g.id)}
          // Groups excluded from "All" get a dashed outline so it's visible at a glance
          // that their numbers aren't part of the totals shown under "All".
          dashed={Boolean(g.excluded_from_all)}
          title={g.excluded_from_all ? `${g.name} is not counted in "All"` : undefined}
        >
          {g.name} ({g.ea_count})
        </FilterButton>
      ))}
    </div>
  );
}

function FilterButton({
  active,
  onClick,
  children,
  dashed = false,
  title,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  dashed?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
        dashed ? "border-dashed" : ""
      } ${
        active
          ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-400"
          : "border-slate-700 text-slate-400 hover:border-slate-600 hover:bg-slate-800 hover:text-slate-200"
      }`}
    >
      {children}
    </button>
  );
}
